import { Component, computed, effect, inject, signal, untracked } from '@angular/core';
import { FormControl } from '@angular/forms';
import {
  DateTimeAdapter,
  OWL_DATE_TIME_FORMATS,
  OWL_DATE_TIME_LOCALE,
  OwlDateTimeModule,
  optionsProviders,
} from '@danielmoncada/angular-datetime-picker';
import { MomentDateTimeAdapter, OwlMomentDateTimeModule } from '@danielmoncada/angular-datetime-picker-moment-adapter';
import moment from 'moment-timezone';

import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { CipoControlBase } from '@shared/components/fields/cipo-control.base';
import { DateTimeFormatService } from '@shared/components/fields/cipo-date-time/date-time-format.service';
import { CipoDateTimeConfig, CipoDateTimeControl } from '@shared/components/fields/common';
import { CipoFormModule } from '@shared/modules/forms.module';
import { ItvDateTimeCustomFormatPipe } from '@shared/pipes';
import { CipoValidators } from '@shared/services/validators.service';
import { TenantStore } from '@signal-store/tenant.store';
import { UserStore } from '@signal-store/user.store';
import { map, of, startWith, switchMap } from 'rxjs';
import { CipoTextViewComponent } from '../cipo-text-view/cipo-text-view.component';

const DATE_TIME_TYPE_CONVERTER = {
  date: 'calendar',
  time: 'timer',
  dateTime: 'both',
} as const;

@Component({
  selector: 'cipo-date-time',
  imports: [
    OwlDateTimeModule,
    OwlMomentDateTimeModule,
    CipoFormModule,
    CipoTextViewComponent,
    ItvDateTimeCustomFormatPipe,
  ],
  providers: [
    ...optionsProviders,
    { provide: DateTimeAdapter, useClass: MomentDateTimeAdapter, deps: [OWL_DATE_TIME_LOCALE] },
    {
      provide: OWL_DATE_TIME_FORMATS,
      useFactory: (dateTimeFormatService: DateTimeFormatService) => dateTimeFormatService.getFormats(),
      deps: [DateTimeFormatService],
    },
    {
      provide: OWL_DATE_TIME_LOCALE,
      useFactory: (dateTimeFormatService: DateTimeFormatService) => dateTimeFormatService.getLocale(),
      deps: [DateTimeFormatService],
    },
  ],
  templateUrl: './cipo-date-time.component.html',
})
export class CipoDateTimeComponent extends CipoControlBase<string | string[], CipoDateTimeConfig> {
  public tenantStore = inject(TenantStore);
  private userStore = inject(UserStore);
  private dateTimeFormatService = inject(DateTimeFormatService);

  /* This is a local signal that saves the proper maxDate, based on both fieldData.maxDate and maxDate.range */
  maxDate = signal<string>(null);

  tenant12hourFormat = computed(() => this.userSettings()?.timeFormat?.includes('A'));
  userSettings = computed(() => this.userStore.calculatedUserSettings());
  pickerType = computed(() => this.fieldData()?.pickerType ?? 'dateTime');
  formattedDate = computed(() => {
    const { dateFormat, timeFormat, datetimeFormat } = this.userSettings();

    if (this.pickerType() === 'date') {
      return dateFormat;
    } else if (this.pickerType() === 'time') {
      return timeFormat;
    }
    return datetimeFormat;
  });
  minDateOwl = computed(() => this.transformIntoOwlLocalDateValue(this.fieldData().minDate) as moment.Moment);
  maxDateOwl = computed(() => this.transformIntoOwlLocalDateValue(this.maxDate()) as moment.Moment);

  dateTimeTypeConverter = DATE_TIME_TYPE_CONVERTER;
  dateTimeControl = new FormControl<moment.Moment | moment.Moment[]>(null);

  protected outerControlErrors = toSignal(
    toObservable(this.control).pipe(
      switchMap(control =>
        control
          ? control.statusChanges.pipe(
              startWith(control.status),
              map(() => control.errors),
            )
          : of(null),
      ),
    ),
  );

  outerCustomError = computed(() => this.outerControlErrors()?.['custom'] ?? null);

  constructor() {
    super();
    effect(() => this.setMinDate(this.deepFieldData.minDate()));
    /* What do we want to happen when maxDate changes? should we reset the input? */
    effect(() => this.setMaxDate(this.deepFieldData.maxDate()));

    effect(() => {
      const newValue = this.formValue();
      this.dateTimeControl.setValue(this.transformIntoOwlLocalDateValue(newValue), { emitEvent: false });
    });
    effect(() => {
      const control = this.control();
      untracked(() => this.onControlChange(control));
    });

    effect(() => {
      const touched = this.controlTouched();
      if (touched) {
        this.dateTimeControl.markAsTouched({ emitEvent: false });
      } else {
        this.dateTimeControl.markAsUntouched({ emitEvent: false });
      }
    });

    effect(() => {
      const customError = this.outerControlErrors()?.['custom'] ?? null;
      const currentCustom = (this.dateTimeControl.errors ?? {})['custom'] ?? null;
      if (customError === currentCustom) return;
      if (customError !== null) {
        const current = this.dateTimeControl.errors ?? {};
        this.dateTimeControl.setErrors({ ...current, custom: customError });
      } else {
        const { custom: _, ...rest } = this.dateTimeControl.errors ?? {};
        this.dateTimeControl.setErrors(Object.keys(rest).length ? rest : null);
      }
    });
  }

  changed(date: moment.Moment) {
    if (this.pickerType() === 'time' || this.fieldData().selectMode !== 'range' || !this.fieldData().maxRange) {
      return;
    }
    const rangeEndDate = moment(date).add(this.fieldData().maxRange, 'day');
    const tempMaxDate = moment.min([rangeEndDate, moment(this.fieldData().maxDate)]);
    this.setMaxDate((this.fieldData().maxDate ? tempMaxDate : rangeEndDate).toISOString());
  }

  resetMaxDate() {
    this.setMaxDate(this.fieldData().maxDate);
  }

  private setMaxDate(date: string) {
    this.maxDate.set(date);
    if (!date) {
      this.validators.delete('maxDate');
    } else {
      this.validators.set('maxDate', CipoValidators.maxDate(date));
    }
    this.updateValidators();
  }

  private setMinDate(date: string) {
    if (!date) {
      this.validators.delete('minDate');
    } else {
      this.validators.set('minDate', CipoValidators.minDate(date));
    }
    this.updateValidators();
  }

  private onControlChange(control: CipoDateTimeControl) {
    // Setup the internal control to sync with the original control
    // it is used to interface with the owl-date-time-picker that need to show values in the user time zone and return UTC ISO strings
    this.setupInternalControl(control);

    // Sync disabled state between the original control and the internal control
    control.registerOnDisabledChange(isDisabled => {
      if (isDisabled) {
        this.dateTimeControl.disable({ emitEvent: false });
      } else {
        this.dateTimeControl.enable({ emitEvent: false });
      }
    });

    // Initialize the disabled state
    if (control.disabled) {
      this.dateTimeControl.disable({ emitEvent: false });
    }

    // Re-evaluate the validity of the control
    control.updateValueAndValidity({ emitEvent: false });
  }

  private setupInternalControl(control: CipoDateTimeControl) {
    // Create a clone FormControl for internal use
    // the value of this control is in the user's local time zone
    // the values has to be a Moment object to preserve and handle the correct timezone
    const internalControl = new FormControl(this.transformIntoOwlLocalDateValue(control.value));

    // Sync changes from the internal control to the original control
    internalControl.valueChanges.subscribe(value => {
      // getting the ISO string in UTC format from the internal control value (owl date time picker)
      const newValue = this.getValueAsIso(value);
      // Avoid emitting if the value is the same
      if (this.dateTimeFormatService.isEqual(newValue, control.value)) {
        return;
      }
      // emit the new value to the original control
      control.setValue(newValue);
      control.markAsDirty();
      control.markAsTouched();
      this.onblur.emit();
    });

    this.updateValidators(internalControl);
    // Replace the original control with the internal control in the template
    this.dateTimeControl = internalControl;
  }

  private transformIntoOwlLocalDateValue(input: string | string[]): moment.Moment | moment.Moment[] {
    const isArray = Array.isArray(input);
    const inputValues = isArray ? input : [input];
    const values = inputValues.map(inputValue =>
      this.dateTimeFormatService.transformIntoOwlLocalDateValue(
        inputValue,
        this.pickerType(),
        this.userStore.userData().ianaTimeZoneId,
      ),
    );
    return isArray ? values : values[0];
  }

  private getValueAsIso(input: moment.Moment | moment.Moment[]): string | string[] {
    const isArray = Array.isArray(input);
    const inputValues = isArray ? input : [input];
    const values = inputValues.map(inputValue =>
      this.dateTimeFormatService.getValueAsIso(inputValue, this.pickerType(), this.userStore.userData().ianaTimeZoneId),
    );
    return isArray ? values : values[0];
  }
}
